import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '@/lib/analytics';
import { useAnalyticsConsent } from '@/contexts/CookieConsentContext';
import { IS_DEV } from '@/config/analytics';

/**
 * Hook to track page views with Google Analytics 4
 * Respects cookie consent preferences and only tracks when analytics consent is granted
 */
export function usePageView() {
  const location = useLocation();
  const hasAnalyticsConsent = useAnalyticsConsent();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    // Only track if user has granted analytics consent
    if (!hasAnalyticsConsent) {
      if (IS_DEV) {
        console.log('[Analytics] Page view not tracked: analytics consent denied');
      }
      return;
    }

    const currentPath = location.pathname + location.search;
    
    // Avoid tracking the same page multiple times
    if (previousPath.current === currentPath) {
      return;
    }

    // Track page view
    const pageTitle = document.title || 'ChinaHospital';
    trackPageView(currentPath, pageTitle);

    // Update previous path
    previousPath.current = currentPath;

    if (IS_DEV) {
      console.log('[Analytics] Page view tracked:', {
        path: currentPath,
        title: pageTitle,
        consent: hasAnalyticsConsent,
      });
    }
  }, [location, hasAnalyticsConsent]);

  // Return current consent status for optional use in components
  return { hasAnalyticsConsent };
}

/**
 * Convenience hook for components that need to know if analytics is available
 */
export function useAnalytics() {
  const { hasAnalyticsConsent } = usePageView();
  return { isAnalyticsAvailable: hasAnalyticsConsent };
}
